<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                登記候位
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="store-select-section">
      <div class="form-group">
        <label>門市</label>
        <div
          v-if="filterStoreId && selectedStore && !isSelectingStore"
          class="selected-store-compact"
        >
          <div
            class="selected-store-card"
            :class="{ 'is-booking-enabled': selectedStore.bookingEnabled }"
          >
            <div class="selected-store-image-wrapper">
              <img
                v-if="getStoreImage(selectedStore)"
                :src="getStoreImage(selectedStore)"
                :alt="selectedStore.storeName"
                class="selected-store-image"
              />
              <div v-else class="selected-store-image-placeholder">
                <i class="ri-store-2-line"></i>
              </div>
            </div>
            <div class="selected-store-content">
              <div class="selected-store-name-row">
                <div class="selected-store-name">{{ selectedStore.storeName }}</div>
                <span v-if="selectedStore.bookingEnabled" class="booking-badge">已開啟預約</span>
              </div>
              <div class="selected-store-address">{{ formatStoreAddress(selectedStore) }}</div>
            </div>
            <button
              type="button"
              class="btn btn-secondary btn-sm store-copy-btn"
              :disabled="waitlistLinksLoading || !storeWaitlistUrl(selectedStore)"
              :title="storeWaitlistUrl(selectedStore) || waitlistLiffMissing"
              @click="copyStoreWaitlistLink(selectedStore)"
            >
              {{ copiedStoreId === selectedStore.id ? '已複製' : '複製候位連結' }}
            </button>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" @click="startStoreSelection">
            更換門市
          </button>
        </div>
        <div v-else-if="storesLoading" class="form-hint">載入門市中...</div>
        <div v-else-if="stores.length === 0" class="form-hint">尚無已開啟預約的門市。</div>
        <div v-else class="store-picker">
          <div
            v-for="store in stores"
            :key="store.id"
            class="store-picker-btn"
            role="button"
            tabindex="0"
            :class="{
              'is-selected': filterStoreId === store.id,
              'is-booking-enabled': store.bookingEnabled
            }"
            @click="selectStore(store.id)"
            @keydown.enter.prevent="selectStore(store.id)"
            @keydown.space.prevent="selectStore(store.id)"
          >
            <div class="store-card-image-wrapper">
              <img
                v-if="getStoreImage(store)"
                :src="getStoreImage(store)"
                :alt="store.storeName"
                class="store-card-image"
              />
              <div v-else class="store-card-image-placeholder">
                <i class="ri-store-2-line"></i>
              </div>
            </div>
            <div class="store-card-content">
              <div class="store-card-name-row">
                <div class="store-card-name">{{ store.storeName }}</div>
                <span v-if="store.bookingEnabled" class="booking-badge">已開啟預約</span>
              </div>
              <div class="store-card-address">{{ formatStoreAddress(store) }}</div>
              <button
                type="button"
                class="btn btn-secondary btn-sm store-copy-btn"
                :disabled="waitlistLinksLoading || !storeWaitlistUrl(store)"
                :title="storeWaitlistUrl(store) || waitlistLiffMissing"
                @click.stop="copyStoreWaitlistLink(store)"
              >
                {{ copiedStoreId === store.id ? '已複製' : '複製候位連結' }}
              </button>
            </div>
          </div>
        </div>
        <p class="form-hint">只顯示已開啟預約的門市。選擇門市後顯示該門市的候位。</p>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="waiting">等待中</option>
          <option value="notified">已通知</option>
          <option value="seated">已入座</option>
          <option value="cancelled">已取消</option>
          <option value="no_show">未出席</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchWaitlist">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>排隊號碼</th>
            <th>姓名</th>
            <th>電話</th>
            <th>人數</th>
            <th>狀態</th>
            <th>登記時間</th>
            <th>等待時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="waitlist.length === 0">
            <td colspan="8" class="empty-state">
              {{ filterStoreId ? '沒有找到候位記錄' : '請先選擇門市' }}
            </td>
          </tr>
          <tr v-for="item in waitlist" :key="item.id">
            <td>{{ item.queueNumber || '-' }}</td>
            <td>{{ item.guestName || '-' }}</td>
            <td>{{ item.guestPhone || '-' }}</td>
            <td>{{ item.guestCount || 0 }}人</td>
            <td>
              <span :class="getStatusBadgeClass(item.status)">
                {{ getStatusText(item.status) }}
              </span>
            </td>
            <td>{{ formatDateTime(item.joinedAt) }}</td>
            <td>{{ formatWaitTime(item.actualWaitTime) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  v-if="item.status === 'waiting'"
                  class="btn btn-sm btn-info"
                  @click="handleNotify(item)"
                  title="通知"
                >
                  <i class="ri-notification-line"></i>
                </button>
                <button
                  v-if="item.status === 'notified'"
                  class="btn btn-sm btn-success"
                  @click="handleSeat(item)"
                  title="安排入座"
                >
                  <i class="ri-check-line"></i>
                </button>
                <button
                  v-if="item.status !== 'seated' && item.status !== 'cancelled'"
                  class="btn btn-sm btn-danger"
                  @click="handleCancel(item)"
                  title="取消"
                >
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { reservationService } from '@/api/modules/reservations.js';
import { storeService } from '@/api/modules/stores.js';
import lineConfigApi from '@/api/modules/lineConfig';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientWaitlist',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('候位管理');
    const waitlist = ref([]);
    const stores = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const filterStoreId = ref('');
    const isSelectingStore = ref(true);
    const storesLoading = ref(false);
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);

    const totalPages = computed(() => Math.ceil(total.value / pageSize.value));
    const waitlistLinksLoading = ref(false);
    const lineConfigs = ref([]);
    const copiedStoreId = ref('');
    const selectedStore = computed(() => stores.value.find((store) => store.id === filterStoreId.value) || null);

    const storeWaitlistUrl = (store) => {
      if (!store?.id) return '';
      const configs = lineConfigs.value.filter((config) => config.isActive !== false);
      const config = (configs.length ? configs : lineConfigs.value)[0];
      if (!config?.commonLiffId) return '';
      return `https://liff.line.me/${config.commonLiffId}/waitlist/${store.id}`;
    };

    const waitlistLiffMissing = computed(() => {
      if (!lineConfigs.value.length) return '尚未設定 LINE 官方帳號，請先到 LINE 設定建立。';
      return '請先到 LINE 設定填寫共用入口 LIFF ID';
    });

    const loadWaitlistLinks = async () => {
      waitlistLinksLoading.value = true;
      try {
        const response = await lineConfigApi.list();
        const items = response?.data?.data?.items ?? response?.data?.items ?? [];
        lineConfigs.value = items.map((config) => {
          const liffIds = Array.isArray(config.liffIds) ? config.liffIds : [];
          return {
            configId: config.id,
            configName: config.configName || 'LINE 官方帳號',
            channelId: config.messagingApi?.channelId || config.messagingChannelId || '',
            commonLiffId: liffIds[0] || '',
            isActive: config.isActive
          };
        });
      } catch {
        lineConfigs.value = [];
      } finally {
        waitlistLinksLoading.value = false;
      }
    };

    const copyStoreWaitlistLink = async (store) => {
      const url = storeWaitlistUrl(store);
      if (!url) {
        showError(waitlistLiffMissing.value);
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch (err) {
        const input = document.createElement('textarea');
        input.value = url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      copiedStoreId.value = store.id;
      setTimeout(() => {
        if (copiedStoreId.value === store.id) copiedStoreId.value = '';
      }, 2000);
    };

    const fetchStores = async () => {
      storesLoading.value = true;
      try {
        const response = await storeService.getStores({
          page: 1,
          pageSize: 200,
          sortBy: 'storeName',
          order: 'asc',
          bookingEnabled: true
        });
        if (response.success && response.data?.items) {
          stores.value = response.data.items.filter((store) => store.bookingEnabled);
        } else {
          console.warn('[Waitlist] No stores data in response');
        }
      } catch (err) {
        console.error('[Waitlist] Failed to fetch stores:', err);
      } finally {
        storesLoading.value = false;
      }
    };

    const selectStore = async (storeId) => {
      if (!storeId) return;
      const changed = filterStoreId.value !== storeId;
      filterStoreId.value = storeId;
      isSelectingStore.value = false;
      if (!changed) return;
      currentPage.value = 1;
      await fetchWaitlist();
    };

    const startStoreSelection = () => {
      isSelectingStore.value = true;
    };

    const getStoreImage = (store) => {
      if (!store || !Array.isArray(store.storeImages)) return '';
      return store.storeImages.find((url) => typeof url === 'string' && url.trim()) || '';
    };

    const formatStoreAddress = (store) => {
      if (!store) return '未提供地址';
      const parts = [store.city, store.district, store.street]
        .filter((part) => typeof part === 'string' && part.trim())
        .map((part) => part.trim());
      return parts.length > 0 ? parts.join('') : '未提供地址';
    };

    const fetchWaitlist = async () => {
      if (!filterStoreId.value) {
        waitlist.value = [];
        total.value = 0;
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
          status: filterStatus.value || undefined
        };

        const response = await reservationService.getWaitlist(filterStoreId.value, params);
        
        if (response.success && response.data) {
          waitlist.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;
        } else {
          throw new Error('查詢失敗');
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '查詢候位記錄失敗';
        console.error('[Waitlist] Failed to fetch waitlist:', err);
      } finally {
        loading.value = false;
      }
    };

    const handleCreate = () => {
      if (!filterStoreId.value) {
        showError('請先選擇門市');
        return;
      }
      router.push(`/client/waitlist/create?storeId=${filterStoreId.value}`);
    };

    const handleNotify = async (item) => {
      try {
        await reservationService.updateWaitlistStatus(item.id, 'notified', {});
        showSuccess('已通知顧客');
        fetchWaitlist();
      } catch (err) {
        showError(err.response?.data?.message || '通知失敗');
      }
    };

    const handleSeat = async (item) => {
      try {
        await reservationService.updateWaitlistStatus(item.id, 'seated', {});
        showSuccess('已安排入座');
        fetchWaitlist();
      } catch (err) {
        showError(err.response?.data?.message || '安排入座失敗');
      }
    };

    const handleCancel = async (item) => {
      const confirmed = await showDeleteConfirm('確定要取消此候位嗎？');
      if (!confirmed) return;

      try {
        await reservationService.updateWaitlistStatus(item.id, 'cancelled', {});
        showSuccess('候位已取消');
        fetchWaitlist();
      } catch (err) {
        showError(err.response?.data?.message || '取消候位失敗');
      }
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchWaitlist();
    };

    const goToPage = (page) => {
      currentPage.value = page;
      fetchWaitlist();
    };

    const getStatusText = (status) => {
      const statusMap = {
        'waiting': '等待中',
        'notified': '已通知',
        'seated': '已入座',
        'cancelled': '已取消',
        'no_show': '未出席'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        'waiting': 'badge badge-warning',
        'notified': 'badge badge-info',
        'seated': 'badge badge-success',
        'cancelled': 'badge badge-danger',
        'no_show': 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDateTime = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    };

    const formatWaitTime = (minutes) => {
      if (!minutes) return '-';
      if (minutes < 60) {
        return `${minutes}分鐘`;
      }
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}小時${mins}分鐘`;
    };

    onMounted(() => {
      fetchStores();
      loadWaitlistLinks();
    });

    return {
      pageTitle,
      waitlist,
      stores,
      loading,
      error,
      filterStoreId,
      isSelectingStore,
      storesLoading,
      filterStatus,
      currentPage,
      totalPages,
      total,
      waitlistLinksLoading,
      waitlistLiffMissing,
      selectedStore,
      copiedStoreId,
      storeWaitlistUrl,
      copyStoreWaitlistLink,
      selectStore,
      startStoreSelection,
      getStoreImage,
      formatStoreAddress,
      handleCreate,
      handleNotify,
      handleSeat,
      handleCancel,
      handleFilterChange,
      goToPage,
      fetchWaitlist,
      getStatusText,
      getStatusBadgeClass,
      formatDateTime,
      formatWaitTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1000px;
}

.store-copy-btn {
  flex-shrink: 0;
  margin-top: 8px;
}

.selected-store-card .store-copy-btn {
  margin-top: 0;
  margin-left: auto;
}

.store-select-section {
  margin-bottom: 4px;
}

.store-picker {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.store-picker-btn {
  border: 1px solid #dbe1ea;
  background: #fff;
  color: #111827;
  border-radius: 10px;
  padding: 0;
  text-align: left;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.store-picker-btn:hover {
  border-color: #409eff;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.12);
}

.store-picker-btn.is-selected {
  border-color: #409eff;
  background: #f6faff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.store-picker-btn.is-booking-enabled {
  border-color: #16a34a;
  background: #f0fdf4;
}

.store-picker-btn.is-booking-enabled:hover {
  border-color: #15803d;
  box-shadow: 0 8px 20px rgba(22, 163, 74, 0.18);
}

.store-picker-btn.is-booking-enabled.is-selected {
  border-color: #15803d;
  background: #dcfce7;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.28);
}

.store-card-name-row,
.selected-store-name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.booking-badge {
  flex-shrink: 0;
  background: #16a34a;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 999px;
}

.store-card-image-wrapper {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
}

.store-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.store-card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 28px;
}

.store-card-content {
  padding: 10px 12px 12px;
}

.store-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.store-card-address {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  min-height: 18px;
}

.selected-store-compact {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-store-card {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  padding: 8px 10px;
  background: #f8fbff;
  max-width: 520px;
}

.selected-store-card.is-booking-enabled {
  border-color: #16a34a;
  background: #f0fdf4;
}

.selected-store-image-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  flex-shrink: 0;
}

.selected-store-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.selected-store-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 20px;
}

.selected-store-content {
  min-width: 0;
}

.selected-store-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.selected-store-address {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .selected-store-compact {
    flex-direction: column;
    align-items: stretch;
  }

  .selected-store-card {
    max-width: none;
  }
}
</style>
